import React, { useEffect, useReducer, useContext } from "react";
import "./../common/Insights.scss";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import translate from "@/i18n/translate";
import { sendRequest } from "../../common/http";
import { WmButton, WmOption, WmSelect } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import { formatDateFullYear } from "../common/helper";
import { openFlyout } from "../../../src/static/js/commons";
import {
  WmProgressMonitor,
  WmProgressSlice,
  WmProgressIndicator,
} from "@watermarkinsights/ripple-react";
import OpenDrillDownViewResponseRate from "./OpenDrillDownViewResponseRate";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import { ReportingYear } from "../common/interfaces/Interface";

interface PreviousProject {
  diffInResponseRate: number;
  isDiffPositive: boolean;
  responseRate: number;
  title: string;
}

interface ResponseRateState {
  average: number;
  responded: number;
  averageDifference: number;
  isAverageDifferencePositive: boolean;
  previousProject: PreviousProject | null;
  awaiting: string;
}

export interface ResponseRateProps {
  selectedReportingYear: ReportingYear;

  onComponentLoad: (type: string) => void;
  resetLoadedComponent: (type: string) => void;
  organizationView: boolean;
}

interface CourseEvaluationState {
  endDate: string;
  projectStatus: number | null;
  startDate: string;
  title: string;
  uuid: number | null;
  surveyId: number | null;
  daysRemaining: number;
  default: boolean;
}

export interface DrilldownHierarchy {
  children: DrilldownHierarchy[];
  enrollments: number;
  level: number;
  name: string;
  nonRespondents: number;
  rate: number;
  respondents: number;
  uuid: number;
}

interface ResponseRateInitialState {
  selectedCourseEvaluation: CourseEvaluationState;
  isDrilldownOpen: boolean;
  courseEvaluations: CourseEvaluationState[];
  awaitingResponseRate: string | number;
  respondedResponseRate: string | number;
  averageResponseRate: number | null;
  averageDifference: number;
  isAverageDifferencePositive: boolean;
  previousProject: PreviousProject | null;
  hierarchy: DrilldownHierarchy | object;
  showTable: boolean;
  loader: boolean;
  projectsApiError: boolean;
  responseRateApiError: boolean;
  resultsHomePath: string;
}

interface ResponseRateAction {
  type: string;
  selectedCourseEvaluation: CourseEvaluationState;
  isDrilldownOpen: boolean;
  courseEvaluations: CourseEvaluationState[];
  awaitingResponseRate: string | number;
  respondedResponseRate: string | number;
  averageResponseRate: number | null;
  averageDifference: number;
  isAverageDifferencePositive: boolean;
  previousProject: PreviousProject | null;
  hierarchy: DrilldownHierarchy | {};
  showTable: boolean;
  loader: boolean;
  projectsApiError: boolean;
  responseRateApiError: boolean;
  resultsHomePath: string;
}

const defaultCourseEvaluation = () => {
  return {
    endDate: "",
    projectStatus: null,
    startDate: "",
    title: "",
    uuid: null,
    surveyId: null,
    daysRemaining: 0,
    default: false,
  };
};
const responseRateDefaultAction = {
  type: "",
  selectedCourseEvaluation: defaultCourseEvaluation(),
  isDrilldownOpen: false,
  courseEvaluations: [],
  awaitingResponseRate: 0,
  respondedResponseRate: 0,
  averageResponseRate: null,
  averageDifference: 0,
  isAverageDifferencePositive: true,
  previousProject: null,
  hierarchy: {},
  showTable: false,
  loader: true,
  projectsApiError: false,
  responseRateApiError: false,
  resultsHomePath: "",
};

export default function ResponseRate({
  selectedReportingYear,
  onComponentLoad,
  resetLoadedComponent,
  organizationView,
}: ResponseRateProps) {
  const getSelectedCourseEvaluation = (
    courseEvaluations: CourseEvaluationState[]
  ) => {
    if (
      courseEvaluations[selectedReportingYear["uuid"]] !== undefined &&
      courseEvaluations[selectedReportingYear["uuid"]].length > 0
    ) {
      return courseEvaluations[selectedReportingYear["uuid"]].filter(
        (ce: CourseEvaluationState) => ce.default
      )[0];
    } else {
      return defaultCourseEvaluation();
    }
  };

  const { institution } = useContext<AppContextInterface>(AppContext);

  const initialState: ResponseRateInitialState = {
    selectedCourseEvaluation: defaultCourseEvaluation(),
    isDrilldownOpen: false,
    courseEvaluations: [],
    awaitingResponseRate: 0,
    respondedResponseRate: 0,
    averageResponseRate: null,
    averageDifference: 0,
    isAverageDifferencePositive: true,
    previousProject: null,
    hierarchy: {},
    showTable: false,
    loader: true,
    projectsApiError: false,
    responseRateApiError: false,
    resultsHomePath: "",
  };
  const reducer = (
    state: ResponseRateInitialState,
    action: ResponseRateAction
  ) => {
    switch (action.type) {
      case "setCourseEvaluations":
        return {
          ...state,
          courseEvaluations: action.courseEvaluations,
          selectedCourseEvaluation: getSelectedCourseEvaluation(
            action.courseEvaluations
          ),
        };
      case "setResponseRate":
        return {
          ...state,
          loader: false,
          awaitingResponseRate: action.awaitingResponseRate,
          respondedResponseRate: action.respondedResponseRate,
          averageResponseRate: action.averageResponseRate,
          averageDifference: action.averageDifference,
          isAverageDifferencePositive: action.isAverageDifferencePositive,
          previousProject: action.previousProject,
        };
      case "showLoader":
        return {
          ...state,
          loader: true,
        };
      case "selectCourseEvaluation":
        return {
          ...state,
          selectedCourseEvaluation: action.selectedCourseEvaluation,
        };
      case "ToggleDrilldown":
        return { ...state, isDrilldownOpen: !state.isDrilldownOpen };
      case "OpenDrilldown":
        return { ...state, isDrilldownOpen: true };
      case "CloseDrilldown":
        return { ...state, isDrilldownOpen: false };
      case "setHierarchy":
        return {
          ...state,
          hierarchy: action.hierarchy,
          showTable: true,
        };
      case "showTable":
        return {
          ...state,
          showTable: action.showTable,
        };
      case "setResponseRateApiError":
        return {
          ...state,
          responseRateApiError: action.responseRateApiError,
          loader: false,
        };
      case "setProjectsApiError":
        return {
          ...state,
          projectsApiError: action.projectsApiError,
          loader: false,
        };
      case "setResultsHomePath":
        return {
          ...state,
          resultsHomePath: action.resultsHomePath,
        };
      default:
        return state;
    }
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const setIsDrilldownOpen = () => {
    dispatch({ ...responseRateDefaultAction, type: "ToggleDrilldown" });
  };

  const openDrilldownView = () => {
    dispatch({ ...responseRateDefaultAction, type: "OpenDrilldown" });
  };
  const closeDrilldown = () => {
    dispatch({ ...responseRateDefaultAction, type: "CloseDrilldown" });
  };
  const resetApiErrors = () => {
    dispatch({
      ...responseRateDefaultAction,
      type: "setResponseRateApiError",
      responseRateApiError: false,
    });
    dispatch({
      ...responseRateDefaultAction,
      type: "setProjectsApiError",
      projectsApiError: false,
    });
  };

  useEffect(() => {
    const applyResult = (result: CourseEvaluationState[]) => {
      dispatch({
        ...responseRateDefaultAction,
        type: "setCourseEvaluations",
        courseEvaluations: result,
      });
    };
    const onError = () => {
      dispatch({
        ...responseRateDefaultAction,
        type: "setProjectsApiError",
        projectsApiError: true,
      });
      onComponentLoad("responseRate");
    };

    sendRequest(
      {
        uri: "/ces/projects",
        camelize: true,
      },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    const applyResult = (result: string) => {
      dispatch({
        ...responseRateDefaultAction,
        type: "setResultsHomePath",
        resultsHomePath: result,
      });
    };
    sendRequest(
      {
        uri: `/ces/results-home-path`,
        camelize: true,
      },
      applyResult
    );
  }, []);

  useEffect(() => {
    if (
      state.selectedCourseEvaluation?.surveyId != null &&
      state.selectedCourseEvaluation?.uuid != null
    ) {
      resetApiErrors();
      dispatch({ ...responseRateDefaultAction, type: "showLoader" });
      resetLoadedComponent("responseRate");
      const applyResult = (result: ResponseRateState) => {
        dispatch({
          ...responseRateDefaultAction,
          type: "setResponseRate",
          awaitingResponseRate: result["awaiting"],
          respondedResponseRate: result["responded"],
          averageResponseRate: result["average"],
          averageDifference: result["averageDifference"],
          isAverageDifferencePositive: result["isAverageDifferencePositive"],
          previousProject: result["previousProject"],
        });
        onComponentLoad("responseRate");
      };
      const onError = () => {
        dispatch({
          ...responseRateDefaultAction,
          type: "setResponseRateApiError",
          responseRateApiError: true,
        });
        onComponentLoad("responseRate");
      };
      sendRequest(
        {
          uri: `/ces/response_rate/survey/${state.selectedCourseEvaluation.surveyId}/project/${state.selectedCourseEvaluation.uuid}`,
          camelize: true,
        },
        applyResult,
        onError
      );
    } else {
      onComponentLoad("responseRate");
    }
  }, [state.selectedCourseEvaluation]);

  const getDrilldownHierarchy = () => {
    dispatch({
      ...responseRateDefaultAction,
      type: "showTable",
      showTable: false,
    });
    const applyResult = (result: DrilldownHierarchy) => {
      dispatch({
        ...responseRateDefaultAction,
        type: "setHierarchy",
        hierarchy: result,
      });
    };
    openDrilldownView();
    sendRequest(
      {
        uri: `/ces/node_response_rate/project/${state.selectedCourseEvaluation.uuid}`,
        camelize: true,
      },
      applyResult
    );
  };

  const openDrilldown = () => {
    getDrilldownHierarchy();
  };

  useEffect(() => {
    dispatch({
      ...responseRateDefaultAction,
      type: "selectCourseEvaluation",
      selectedCourseEvaluation: getSelectedCourseEvaluation(
        state.courseEvaluations
      ),
    });
  }, [selectedReportingYear]);

  const filter = useIntl().formatMessage({
    id: "app.ces.responseRate.filter",
  });

  const openHtmlView = () => {
    const locale = new URLSearchParams(window.location.search).get("locale");
    const isPlanning = window.location.pathname.includes("/planning/");
    const base = isPlanning ? `/insights/planning` : `/insights`;
    let url = `${base}/institution/${institution?.platformUuid ?? ""}/ces/survey/${state.selectedCourseEvaluation.surveyId}/project/${state.selectedCourseEvaluation.uuid}/html`;
    url = locale ? `${url}?locale=${locale}` : url;

    return window.open(url, "_blank");
  };
  const CourseEvaluationSelect = ({
    courseEvaluations,
    selectedCourseEvaluation,
  }: {
    courseEvaluations: CourseEvaluationState[];
    selectedCourseEvaluation: CourseEvaluationState;
  }) => {
    return (
      <WmSelect
        label={useIntl().formatMessage({
          id: "app.ces.responseRate.courseEvaluation",
        })}
        labelPosition="left"
        id="filter-by-course-evaluation"
        search
      >
        {courseEvaluations.map((ce: CourseEvaluationState) => (
          <WmOption
            selected={ce.uuid === selectedCourseEvaluation.uuid}
            onClick={() =>
              dispatch({
                ...responseRateDefaultAction,
                type: "selectCourseEvaluation",
                selectedCourseEvaluation: ce,
              })
            }
            key={ce["uuid"]}
            id={ce["uuid"]?.toString()}
          >
            {ce["title"]}
          </WmOption>
        ))}
      </WmSelect>
    );
  };

  const noCourseBody = () => {
    return state.courseEvaluations[selectedReportingYear["uuid"]].length === 0
      ? "no-course-body"
      : "";
  };

  const CourseEvaluationStatus = ({ status }: { status: number }) => {
    switch (status) {
      case 3:
        return (
          <div className="in-progress-status">
            <div className="title-text">
              {translate("app.drilldown.inProgress")}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="ended-status -completed">
            <div className="title-text">{translate("app.drilldown.ended")}</div>
          </div>
        );
      default:
        return <></>;
    }
  };

  const respondedText = useIntl().formatMessage({
    id: "app.ces.responseRate.responded",
  });
  const popoverRespondedText = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedPopover",
  });
  const popoverAwaitingText = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedAwaitingPopover",
  });
  const popoverButtonText = useIntl().formatMessage({
    id: "app.ces.responseRate.popoverButtonText",
  });
  const awaitingText = useIntl().formatMessage({
    id: "app.ces.responseRate.awaiting",
  });

  const respondedAwaiting = useIntl().formatMessage({
    id: "app.ces.responseRate.respondedAwaiting",
  });
  const responseRate = useIntl().formatMessage({
    id: "app.ces.responseRate.responseRate",
  });
  const responseRateFullWidthLabel = useIntl().formatMessage({
    id: "app.ces.responseRate.overallResponseRate",
  });

  const responseRatesBody = () => {
    return (
      <div className="paper-card">
        <div className="header">
          <div className="info">
            <h3 className="title">
              {translate("app.ces.responseRate.courseEvaluationResponseRate")}
            </h3>
            <p className="description">
              {translate("app.ces.responseRate.description")}
            </p>
          </div>
          {state.courseEvaluations[selectedReportingYear["uuid"]] &&
            state.courseEvaluations[selectedReportingYear["uuid"]].length > 0 &&
            !state.loader &&
            !state.responseRateApiError ? (
            <div>
              <WmButton
                id="export-button"
                tabIndex={0}
                onClick={openHtmlView}
                button-type="secondary"
              >
                {translate("app.exportHtml")}
              </WmButton>
            </div>
          ) : (
            ""
          )}
        </div>
        {state.courseEvaluations[selectedReportingYear["uuid"]] !==
          undefined && (
            <div className={`body ${noCourseBody()}`}>
              {organizationView && (
                <div className="results-filtered-by">
                  <strong>
                    {translate("app.home.showingResultsForAllOrganizations")}
                  </strong>{" "}
                  {institution?.name}
                </div>
              )}
              {state.courseEvaluations[selectedReportingYear["uuid"]].length >
                0 &&
                state.selectedCourseEvaluation &&
                state.selectedCourseEvaluation["startDate"] && (
                  <div className="">
                    <div className="plan-dropdown-container-ces flex-row">
                      <div className="plan-dropdown-ces">
                        <CourseEvaluationSelect
                          courseEvaluations={
                            state.courseEvaluations[selectedReportingYear["uuid"]]
                          }
                          selectedCourseEvaluation={
                            state.selectedCourseEvaluation
                          }
                        />
                      </div>
                    </div>
                    <>
                      <div
                        className={
                          institution?.isPssIntegrated
                            ? "flex-row -justified plan-status-container"
                            : "flex-row status-margin plan-status-container"
                        }
                      >
                        <div className="plan-title">
                          {state.selectedCourseEvaluation["title"]}
                        </div>
                        <CourseEvaluationStatus
                          status={state.selectedCourseEvaluation["projectStatus"]}
                        />
                      </div>
                      <div className="ces-duration">
                        <div className="ces-details">
                          {translate("app.ces.responseRate.details")}
                        </div>
                        <div className="ces-dates">
                          <span className="mdi mdi-calendar-clock"></span>
                          {`${formatDateFullYear(
                            state.selectedCourseEvaluation["startDate"]
                          )} -
                    ${formatDateFullYear(
                            state.selectedCourseEvaluation["endDate"]
                          )}`}
                        </div>
                        {state.selectedCourseEvaluation["daysRemaining"] > 0 && (
                          <div className="ces-days">
                            <span className="mdi mdi-timer-sand"></span>

                            {translate("app.ces.responseRate.daysRemaining", {
                              daysRemaining:
                                state.selectedCourseEvaluation["daysRemaining"],
                            })}
                          </div>
                        )}
                      </div>
                      {state.loader && (
                        <div className="loader">
                          <div className="content content-loader"></div>
                        </div>
                      )}
                      {!state.loader && !state.responseRateApiError && (
                        <div
                          className={
                            institution?.isPssIntegrated
                              ? "response-rate-container"
                              : "response-rate-container non-pss-integrated"
                          }
                        >
                          {institution?.isPssIntegrated && (
                            <div className="response-rate-label">
                              {translate(
                                "app.ces.responseRate.overallResponseRate"
                              )}
                            </div>
                          )}

                          <div className="response-rate-chart">
                            <div className="organization-progress-chart">
                              <WmProgressMonitor
                                id="organization-progress"
                                group-legend={respondedAwaiting}
                                breakpoint={
                                  institution?.isPssIntegrated ? "10px" : ""
                                }
                              >
                                <WmProgressIndicator
                                  label={
                                    institution?.isPssIntegrated
                                      ? responseRate
                                      : responseRateFullWidthLabel
                                  }
                                  onWmProgressIndicatorButtonTriggered={() =>
                                    openDrilldown()
                                  }
                                >
                                  <WmProgressSlice
                                    amount={state.respondedResponseRate?.toString()}
                                    popover-text={popoverRespondedText}
                                    legend={respondedText}
                                    popover-button-text={popoverButtonText}
                                    popover-title={`${state.respondedResponseRate}%`}
                                    title={
                                      state.selectedCourseEvaluation["title"]
                                    }
                                  ></WmProgressSlice>
                                  <WmProgressSlice
                                    amount={state.awaitingResponseRate?.toString()}
                                    popover-text={popoverAwaitingText}
                                    legend={awaitingText}
                                    popover-button-text={popoverButtonText}
                                    popover-title={`${state.awaitingResponseRate}%`}
                                    title={
                                      state.selectedCourseEvaluation["title"]
                                    }
                                  ></WmProgressSlice>
                                </WmProgressIndicator>
                              </WmProgressMonitor>
                            </div>
                          </div>
                          <div className="response-rate-divider">
                            <div className="response-rate-trends">
                              <div className="response-rate-trends-heading">
                                {translate(
                                  "app.ces.responseRate.responseRateTrends"
                                )}
                              </div>
                              {state.averageResponseRate !== null && (
                                <div className="response-rate-trends-container">
                                  <div className="response-rate-all-courses">
                                    <div className="flex-row">
                                      <span
                                        className={`-icons mdi ${arrow(
                                          state.isAverageDifferencePositive
                                        )}`}
                                      ></span>
                                      <div className="comparison-rate-all-courses">
                                        {state.averageDifference}%
                                      </div>
                                    </div>
                                    <div className="response-rate-trends-desc">
                                      {translate(
                                        "app.ces.responseRate.responseRateTrendsDescriptionAllCourses",
                                        {
                                          comparisonRate:
                                            state.averageResponseRate,
                                        }
                                      )}
                                    </div>
                                  </div>
                                  <div className="response-rate-previous-course">
                                    {state.previousProject && (
                                      <div className="flex-row">
                                        <span
                                          className={`-icons mdi ${arrow(
                                            state.previousProject.isDiffPositive
                                          )}`}
                                        ></span>
                                        <div className="comparison-rate-all-courses">
                                          {
                                            state.previousProject
                                              .diffInResponseRate
                                          }
                                          %
                                        </div>
                                      </div>
                                    )}
                                    {state.previousProject && (
                                      <div className="response-rate-trends-desc">
                                        {translate(
                                          "app.ces.responseRate.responseRateTrendsDescriptionPreviousCourse",
                                          {
                                            previousProjectTitle:
                                              state.previousProject.title,
                                            comparisonRate:
                                              state.previousProject.responseRate,
                                          }
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {state.averageResponseRate === null && (
                                <div className="flex-row no-response-rate">
                                  <span className="-icons mdi mdi-timer-sand"></span>
                                  <div className="no-response-rate-text">
                                    {translate(
                                      "app.ces.responseRate.NoResponseRateText"
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {!state.loader && state.responseRateApiError && (
                        <ApiError />
                      )}
                    </>
                  </div>
                )}
              {state.courseEvaluations[selectedReportingYear["uuid"]].length ===
                0 && (
                <div className="no-plans-ces-wrapper">
                  {NoCourseEvaluations()}
                </div>
              )}
            </div>
          )}

        {state.resultsHomePath !== "" && (
          <div className="results-home footer">
            <a
              className="navigation-link"
              href={state.resultsHomePath}
            >
              {translate("app.ces.responseRate.resultsHome")}
            </a>
          </div>
        )}

        {state.courseEvaluations.length === 0 && !state.projectsApiError && (
          <WidgetLoader
            loaderContainerClasses="loader"
            loaderClasses="content"
          />
        )}

        {state.projectsApiError && <ApiError />}

        {state.courseEvaluations["course_evaluation"] === null && (
          <h3 className="title">
            <ApiError />
          </h3>
        )}
      </div>
    );
  };

  return (
    <>
      {institution?.showCourseEvaluation && responseRatesBody()}
      <OpenDrillDownViewResponseRate
        setIsDrilldownOpen={closeDrilldown}
        title={state.selectedCourseEvaluation["title"]}
        remainingDays={state.selectedCourseEvaluation["daysRemaining"]}
        startDate={state.selectedCourseEvaluation["startDate"]}
        endDate={state.selectedCourseEvaluation["endDate"]}
        status={state.selectedCourseEvaluation["projectStatus"]}
        showTable={state.showTable}
        completeHierarchy={state.hierarchy}
        isVisible={state.isDrilldownOpen}
        open={state.isDrilldownOpen}
        id="ces-response-rate-drill-down-view"
      ></OpenDrillDownViewResponseRate>
    </>
  );
}

const NoCourseEvaluations = () => {
  return (
    <>
      <div className="no-plans-container">
        <div className="no-plans-ces">
          <div className="no-plans-text-ces">
            {translate("app.ces.responseRate.noCourseEvaluations")}
          </div>
        </div>

        <div className="no-plans-ces">
          <div className="no-plans-text-select-ces">
            {translate("app.ces.responseRate.SelectDifferentAcademicYear")}
          </div>
        </div>
      </div>
    </>
  );
};

export const arrow = (isDiffPositive: boolean) => {
  return isDiffPositive ? "mdi-arrow-up" : "mdi-arrow-down";
};
